import { useEffect } from 'react'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Icon,
  Image,
  Separator,
  Text,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '@/features/onboarding/shared/useFixedFooterOffset'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { openPanel } from '@/features/adviser-chat/adviser-chat.slice'
import { selectBankingApplicationStatus } from './banking.selectors'
import { BankingApplicationStatus } from './banking.types'
import { useHeardBankingPromo } from './banking-promo.helpers'
import { BankingAccountsPreview } from './BankingAccountsPreview'
import {
  HEARD_BANKING_LEARN_MORE_URL,
  HEARD_BANKING_PROMO_COPY,
  HEARD_BANKING_STANDARD_DISCLAIMER,
} from './banking.constants'
import { InlineLinkText } from '@/features/user/dashboard/feature-announcement/renderInlineLinks'
import {
  OFFER_SOURCES,
  useBankingAnalytics,
} from '@/features/banking/analytics'

const HEARD_BANKING_HERO_IMAGE =
  'https://heard-images.s3.amazonaws.com/assets/card-black-piggybank.svg'

export interface BankingIntroPageProps {
  /** Called when the user clicks "Apply in Minutes" or "Continue" */
  onContinue: () => void
  /** Called when the user clicks "Back" */
  onBack: () => void
  /** Called when the user clicks either "Skip for now" button */
  onSkip: () => void
}

const features = [
  {
    src: 'https://heard-images.s3.amazonaws.com/assets/juggling.svg',
    alt: 'One place for everything',
    title: 'One place for everything',
    description:
      'Banking*, bookkeeping, and taxes all live in Heard. No more logging into your bank, then your bookkeeper, then your tax tool, one login, one view.',
  },
  {
    src: 'https://heard-images.s3.amazonaws.com/assets/bank-auto.svg',
    alt: 'Earn yield on your balance',
    title: 'Earn yield on your balance',
    description:
      'Most business checking accounts pay nothing. Heard Banking earns interest on every dollar. Your money works for you while it sits in your account.',
  },
  {
    src: 'https://heard-images.s3.amazonaws.com/assets/books_lightShadow.svg',
    alt: 'Fully automated bookkeeping',
    title: 'Fully automated bookkeeping',
    description:
      'Transactions flow directly into your books with no manual uploads, no statement chasing. Your bookkeeper and your bank see the same data in real time.',
  },
]

const SEEDLING_ICON_URL =
  'https://heard-images.s3.amazonaws.com/assets/seedling-sm.svg'

export const BankingIntroPage = ({
  onContinue,
  onBack,
  onSkip,
}: BankingIntroPageProps) => {
  const bankingStatus = useReselector(selectBankingApplicationStatus)
  const hasBankingApplication = bankingStatus !== null
  const isApplicationUnderReview =
    bankingStatus === BankingApplicationStatus.PendingReview ||
    bankingStatus === BankingApplicationStatus.AwaitingDocuments
  const promo = useHeardBankingPromo()
  const leftOffset = useFixedFooterOffset()
  const dispatch = useAppDispatch()

  const { trackOfferViewed, trackOfferClicked, trackOfferDismissed } =
    useBankingAnalytics()

  useEffect(() => {
    trackOfferViewed(OFFER_SOURCES.OnboardingBankingIntro)
  }, [trackOfferViewed])

  const handleContinue = () => {
    trackOfferClicked(OFFER_SOURCES.OnboardingBankingIntro)
    onContinue()
  }

  const handleAskHaven = () => {
    dispatch(openPanel())
  }

  const handleSkip = () => {
    trackOfferDismissed(OFFER_SOURCES.OnboardingBankingIntro)
    onSkip()
  }

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex flex-col pb-[96px] max-sm:pb-6">
        <div className="flex flex-col">
          {isApplicationUnderReview && (
            <Alert
              variant="warning"
              title="Your application is under review"
              description="You'll receive an email from us once it's approved."
              className="mb-6"
            />
          )}
          {/* Hero card */}
          <Card
            color={CardColors.SUCCESS}
            childrenClassName="flex flex-col sm:flex-row items-start gap-6 p-6"
            data-testid="banking-intro-hero"
          >
            <div className="flex flex-col gap-4 flex-1">
              {/* title + FDIC stay tight; gap only under the eyebrow */}
              <div className="flex flex-col gap-0">
                <Text
                  variant={TypographyStyles.EYEBROW}
                  color={TextColors.MUTED}
                  className="uppercase tracking-widest mb-2"
                >
                  Free Member Benefit
                </Text>
                <Text
                  variant={TypographyStyles.DISPLAY_SM}
                  color={TextColors.HEADER}
                >
                  {promo.isActive ? (
                    <>
                      {HEARD_BANKING_PROMO_COPY.titlePrefix}
                      <sup>*</sup>
                      {HEARD_BANKING_PROMO_COPY.titleSuffix}
                    </>
                  ) : (
                    'Introducing Heard Banking'
                  )}
                </Text>
                <Text
                  variant={TypographyStyles.BODY_XS}
                  color={TextColors.MUTED}
                >
                  FDIC insurance eligible<sup>*</sup>
                </Text>
              </div>
              <Text variant={TypographyStyles.BODY_SM}>
                {promo.isActive
                  ? promo.deadline
                    ? HEARD_BANKING_PROMO_COPY.body(
                        promo.deadline.date,
                        promo.deadline.time
                      )
                    : HEARD_BANKING_PROMO_COPY.bodyNoDeadline
                  : 'Get free business banking with a checking, and savings account and a debit card right where your bookkeeping and taxes already are. Make it your go-to business account and avoid any manual work.'}
              </Text>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={handleContinue}
                  className="flex items-center gap-0 pr-3"
                >
                  {promo.isActive
                    ? HEARD_BANKING_PROMO_COPY.cta
                    : hasBankingApplication
                      ? 'View application'
                      : 'Apply in Minutes'}
                  <Icon icon={faChevronRight} size="sm" color="white" />
                </Button>
                <Button variant="secondary" onClick={handleSkip}>
                  Skip for now
                </Button>
              </div>
              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                {promo.isActive && promo.disclaimer ? (
                  <InlineLinkText text={promo.disclaimer} />
                ) : (
                  HEARD_BANKING_STANDARD_DISCLAIMER
                )}
              </Text>
            </div>
            <Image
              src={HEARD_BANKING_HERO_IMAGE}
              alt="Heard Banking"
              className="hidden sm:block ml-auto shrink-0 w-52 h-auto object-contain"
            />
          </Card>

          {/* What is Heard Banking */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <Text variant={TypographyStyles.DISPLAY_XS}>
                What is Heard Banking?
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Heard Business Banking<sup>*</sup>, powered by i3 Bank, is a
                free business checking and savings account built right into your
                Heard experience. It plugs into the rest of Heard (bookkeeping,
                tax, and reporting) the same way any other connected account
                does.
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                It isn&apos;t a separate product or a new subscription.
                It&apos;s simply one more way to set up a business bank account.
              </Text>
              <a
                href={HEARD_BANKING_LEARN_MORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackOfferClicked(OFFER_SOURCES.OnboardingBankingIntro)
                }
                className="inline-flex w-fit items-center text-sm font-medium text-neutral-900 underline underline-offset-2 hover:opacity-80"
              >
                Read the details &rarr;
              </a>
            </div>
            <BankingAccountsPreview />
          </div>

          {/* What you get */}
          <div className="mt-14 space-y-6">
            <Text variant={TypographyStyles.DISPLAY_XS}>What you get:</Text>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {features.map((feature) => (
                <div key={feature.title} className="flex flex-col gap-3">
                  <Image
                    src={feature.src}
                    alt={feature.alt}
                    className="w-[180px] h-[180px] object-contain mx-auto"
                  />
                  <Text variant={TypographyStyles.H1}>{feature.title}</Text>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    {feature.description}
                  </Text>
                </div>
              ))}
            </div>
          </div>

          <Card color={CardColors.LEVEL_1} className="mt-14">
            <div className="flex flex-wrap items-center gap-6">
              <Image
                src={SEEDLING_ICON_URL}
                alt=""
                className="size-20 shrink-0"
              />
              <div className="min-w-[150px] flex-1 space-y-2">
                <Text variant={TypographyStyles.H1}>Need a hand?</Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  Haven, our AI assistant, can answer your questions or connect
                  you with the right person.{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 font-medium"
                    onClick={handleAskHaven}
                  >
                    Ask Haven &rarr;
                  </Button>
                </Text>
              </div>
            </div>
          </Card>

          <Separator className="mt-10" />

          {/* Legal disclaimers */}
          <div className="mt-6 space-y-3 pb-8">
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              *Heard is a financial technology company and not a bank. Banking
              services are provided by i3 Bank, Members FDIC. Visa® debit cards
              are issued by i3 Bank, Members FDIC pursuant to a license from
              VISA U.S.A. Inc. and may be used everywhere Visa debit cards are
              accepted.
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              Accounts are eligible for pass-through deposit insurance only to
              the extent pass-through insurance is permitted by the rules and
              regulations of the FDIC, and if the requirements for pass-through
              insurance are satisfied. There may be a risk that pass-through
              deposit insurance is not available because conditions have not
              been satisfied. In such cases, funds may not be fully insured in
              the event the insured depository institution where the funds have
              been deposited were to fail.
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              **The advertised rate is an approximation and is rounded to the
              nearest hundredth of a percent. Annual Percentage Yield (APY) is
              variable and subject to change after account opening. Rate is
              compounded monthly and credited monthly.
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              ***No account or domestic transaction fees. Some foreign
              transaction fees may apply in limited circumstances.
            </Text>
          </div>
        </div>
      </div>

      <OnboardingStickyNav
        leftOffset={leftOffset}
        className="mx-auto max-w-[1200px]"
        onBack={onBack}
        onSkip={handleSkip}
        skipLabel="Skip for Now"
        onContinue={handleContinue}
        continueLabel={hasBankingApplication ? 'View application' : 'Apply Now'}
      />
    </div>
  )
}
